import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertTriangle, Clock, RefreshCw, AlertCircle, CheckCircle, Filter } from 'lucide-react';

const CHECK_TYPE_LABELS = {
  address_check: 'Address',
  employment_check: 'Employment',
  education_check: 'Education',
  criminal_court_check: 'Criminal Court',
  court_advocate_check: 'Criminal Advocate',
  id_check: 'ID Check',
  world_criminal_db: 'World Database',
  drug_test: 'Drug Test',
  credit_check: 'Credit/CIBIL',
  uan_check: 'UAN Check',
  reference_check: 'Reference'
};

export const TATBreachAlertsTab = () => {
  const [breachData, setBreachData] = useState({ checks: [], summary: {}, vendor_stats: [] });
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({
    severity: 'all',
    vendor_id: '',
    check_type: ''
  });
  const [vendors, setVendors] = useState([]);

  const fetchBreachData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.severity !== 'all') params.append('severity', filter.severity);
      if (filter.vendor_id) params.append('vendor_id', filter.vendor_id);
      if (filter.check_type) params.append('check_type', filter.check_type);
      
      const res = await axios.get(`/operations/tat-breaches?${params.toString()}`);
      setBreachData(res.data);
    } catch (error) {
      console.error('Failed to fetch TAT breaches:', error);
      toast.error('Failed to load TAT breach data');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await axios.get('/subvendors');
      setVendors(res.data || []);
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    }
  };

  useEffect(() => {
    fetchBreachData();
    fetchVendors();
  }, []);

  useEffect(() => {
    fetchBreachData();
  }, [filter]);

  const getSeverityBadge = (severity) => {
    if (severity === 'breached') {
      return <Badge className="bg-red-100 text-red-700">🔴 Breached</Badge>;
    }
    return <Badge className="bg-amber-100 text-amber-700">🟡 At Risk</Badge>;
  };

  const getCheckTypeLabel = (checkType) => {
    return CHECK_TYPE_LABELS[checkType] || checkType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="p-6 space-y-6" data-testid="tat-breach-alerts-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-red-700 flex items-center gap-2">
            <AlertTriangle className="w-7 h-7" />
            TAT Breach Alerts
          </h2>
          <p className="text-slate-500 text-sm">Monitor checks that have breached or are at risk of breaching TAT</p>
        </div>
        <Button 
          variant="outline"
          onClick={fetchBreachData}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-red-700">{breachData.summary?.total_breached || 0}</div>
                <div className="text-xs text-red-600">Breached TAT</div>
              </div>
              <AlertCircle className="w-10 h-10 text-red-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-amber-700">{breachData.summary?.total_at_risk || 0}</div>
                <div className="text-xs text-amber-600">At Risk (≤2 days)</div>
              </div>
              <Clock className="w-10 h-10 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-slate-700">{breachData.summary?.total_alerts || 0}</div>
                <div className="text-xs text-slate-600">Total Alerts</div>
              </div>
              <AlertTriangle className="w-10 h-10 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-green-700">{breachData.vendor_stats?.length || 0}</div>
                <div className="text-xs text-green-600">Vendors with Alerts</div>
              </div>
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Filter className="w-5 h-5 text-slate-500" />
            <div className="flex-1 grid grid-cols-3 gap-4">
              <Select
                value={filter.severity}
                onValueChange={(v) => setFilter({...filter, severity: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Severity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Alerts</SelectItem>
                  <SelectItem value="breached">Breached Only</SelectItem>
                  <SelectItem value="at_risk">At Risk Only</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={filter.vendor_id || "all_vendors"}
                onValueChange={(v) => setFilter({...filter, vendor_id: v === "all_vendors" ? "" : v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Vendors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_vendors">All Vendors</SelectItem>
                  {vendors.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filter.check_type || "all_types"}
                onValueChange={(v) => setFilter({...filter, check_type: v === "all_types" ? "" : v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Check Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_types">All Check Types</SelectItem>
                  {Object.entries(CHECK_TYPE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Vendor Stats */}
      {breachData.vendor_stats?.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Vendor-wise Breach Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {breachData.vendor_stats.map((vendor, idx) => (
                <div 
                  key={idx}
                  className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-lg border"
                >
                  <span className="font-medium text-sm">{vendor.vendor_name}</span>
                  {vendor.breached > 0 && (
                    <Badge className="bg-red-100 text-red-700 text-xs">{vendor.breached} breached</Badge>
                  )}
                  {vendor.at_risk > 0 && (
                    <Badge className="bg-amber-100 text-amber-700 text-xs">{vendor.at_risk} at risk</Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alerts Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-red-50">
                <TableHead>Severity</TableHead>
                <TableHead>Case ID</TableHead>
                <TableHead>Employee</TableHead>
                <TableHead>Check Type</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Client</TableHead>
                <TableHead className="text-center">TAT (Days)</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead>Alert</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-12">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-slate-400" />
                    <p className="text-slate-500">Loading alerts...</p>
                  </TableCell>
                </TableRow>
              ) : breachData.checks?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-12">
                    <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
                    <p className="text-green-600 font-medium">All Clear!</p>
                    <p className="text-slate-500 text-sm">No TAT breaches or at-risk checks found</p>
                  </TableCell>
                </TableRow>
              ) : (
                breachData.checks.map((check, idx) => (
                  <TableRow 
                    key={idx} 
                    className={check.severity === 'breached' ? 'bg-red-50/50' : 'bg-amber-50/50'}
                  >
                    <TableCell>{getSeverityBadge(check.severity)}</TableCell>
                    <TableCell className="font-mono text-xs">{check.case_id?.substring(0, 8)}...</TableCell>
                    <TableCell className="font-medium">{check.employee_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{getCheckTypeLabel(check.check_type)}</Badge>
                    </TableCell>
                    <TableCell>{check.vendor_name || 'Unassigned'}</TableCell>
                    <TableCell className="text-sm text-slate-600">{check.client_name}</TableCell>
                    <TableCell className="text-center">
                      <span className="font-mono">{check.vendor_tat}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="text-xs">{check.status}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className={`text-sm font-medium ${check.severity === 'breached' ? 'text-red-600' : 'text-amber-600'}`}>
                        {check.alert_message}
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default TATBreachAlertsTab;
